import moment from "moment";
import React from "react";
import { ON_OBJECT_POST_SAVE } from "./constants";
import { Button } from "antd";

import { staticAntd } from "@/utils/antdStatic";

export function formatMomentDate(
  date: string | Date | null | undefined,
  format: string = "DD/MM/YYYY",
): string | undefined {
  if (date) {
    return moment(date).format(format);
  }
  return undefined;
}

export function formatMomentDateTime(
  date: string | Date | null | undefined,
  format: string = "DD/MM/YYYY HH:mm",
): string | undefined {
  return formatMomentDate(date, format);
}

// AU mobile MSNs (04xx xxx xxx) get display grouping; anything else as-is.
export function formatMsn(msn: string | null | undefined): string {
  if (!msn) return "- -";
  return /^0\d{9}$/.test(msn)
    ? msn.replace(/^(\d{4})(\d{3})(\d{3})$/, "$1 $2 $3")
    : msn;
}

export function generateRandomString(length: number): string {
  let result = "";
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
}

interface RgbColor {
  r: number;
  g: number;
  b: number;
}

function hexToRgb(hex: string): RgbColor | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

export function shadeColor(
  color: string,
  percent: number,
  alpha: number = 1,
): string {
  let R = parseInt(color.substring(1, 3), 16);
  let G = parseInt(color.substring(3, 5), 16);
  let B = parseInt(color.substring(5, 7), 16);

  if (R === 0) R = 32;
  if (G === 0) G = 32;
  if (B === 0) B = 32;

  R = parseInt(String((R * (100 + percent)) / 100));
  G = parseInt(String((G * (100 + percent)) / 100));
  B = parseInt(String((B * (100 + percent)) / 100));

  R = R < 255 ? R : 255;
  G = G < 255 ? G : 255;
  B = B < 255 ? B : 255;

  const RR =
    R.toString(16).length === 1 ? "0" + R.toString(16) : R.toString(16);
  const GG =
    G.toString(16).length === 1 ? "0" + G.toString(16) : G.toString(16);
  const BB =
    B.toString(16).length === 1 ? "0" + B.toString(16) : B.toString(16);

  const colors = hexToRgb("#" + RR + GG + BB);

  return `rgba(${colors!.r}, ${colors!.g}, ${colors!.b}, ${alpha})`;
}

interface WebSocketMessage {
  type: string;
  user_id?: number;
  [key: string]: any;
}

interface Instance {
  id: string | number;
  [key: string]: any;
}

export const onWebSocketMessage = (
  msg: WebSocketMessage,
  instance: Instance,
  userId?: number,
  read?: (id: string | number) => void,
): void => {
  // Thirty-three of the thirty-four call sites cannot supply these. They pass
  // `(msg, userId, read) => onWebSocketMessage(msg, instance, userId, read)`
  // to `WebSocketWrapper`, and both `WebSocketContext`s invoke `onMessage`
  // with the parsed message alone — so the arrow's second and third arguments
  // are always undefined. Without `read` there is no refresh action to offer
  // and the button below would throw on click; without `userId` the change
  // cannot be attributed, so the notification could not be suppressed for the
  // user's own edit. Stay quiet until those call sites are plumbed.
  //
  // `ChangeForm` is the exception: it bypasses the wrapper and renders the
  // DataV2 `WebSocketContextProvider` itself, handing it
  // `(msg) => onWebSocketMessage(msg, instance, user.id, read)` with a real
  // `read` and `user.id` closed over. It passes this guard and keeps behaving
  // as it always has — netsg-apps/netportal#2051 did not change it.
  if (!read) {
    return;
  }

  const hideNotification = msg.user_id && msg.user_id === userId;

  switch (msg.type) {
    case ON_OBJECT_POST_SAVE:
      if (!hideNotification) {
        const key = `instanceUpdate${Date.now()}`;

        staticAntd.notification?.info({
          key: key,
          message: "Object information was updated",
          duration: 5,
          btn: (
            <Button
              type={"primary"}
              onClick={() => {
                read(instance.id);
                staticAntd.notification?.destroy(key);
              }}
            >
              Refresh window
            </Button>
          ),
        });
      }
      break;

    default:
      break;
  }
};

export const bToMb = (value: number): string => {
  const MB = 1024 * 1024;
  return (value / MB).toFixed(2);
};

export const bToGb = (value: number): string => {
  const GB = 1024 * 1024 * 1024;
  return (value / GB).toFixed(2);
};

export const getProvisioningStatusTagColor = (
  status: string,
): string | undefined => {
  let color: string | undefined;
  switch (status) {
    case "ACTIVE":
      color = "green";
      break;
    case "PROVISIONING":
      color = "blue";
      break;
    case "TERMINATED":
      color = "red";
      break;
    case "ON_HOLD":
      color = "orange";
      break;
    default:
      break;
  }
  return color;
};

// Hardware lifecycle status of a device model — derived server-side from the
// vendor EOL dates (#1897), never stored. Label and colour live together here
// so every surface showing the badge reads the same.
export const DEVICE_LIFECYCLE_STATUS_LABELS: Record<string, string> = {
  ACTIVE: "Active",
  EOS_ANNOUNCED: "End of sale announced",
  EOL_UPCOMING: "End of life upcoming",
  EOL: "End of life",
  UNKNOWN: "Unknown",
};

export const getDeviceLifecycleStatusTagColor = (
  status: string,
): string | undefined => {
  let color: string | undefined;
  switch (status) {
    case "ACTIVE":
      color = "green";
      break;
    case "EOS_ANNOUNCED":
      color = "gold";
      break;
    case "EOL_UPCOMING":
      color = "orange";
      break;
    case "EOL":
      color = "red";
      break;
    // UNKNOWN stays uncoloured on purpose: nobody has recorded the vendor
    // dates yet, which is not the same as the model being in support.
    default:
      break;
  }
  return color;
};

export const isValidVoiceBillingPhoneNumber = (
  value: string | number | null | undefined,
): boolean => {
  return !!(
    value &&
    (value.toString().length === 10 || value.toString().length === 6) &&
    /^\d+$/.test(value.toString())
  );
};

export const createVoiceBillingPhoneNumberFromInteger = (
  i: string | number,
  count: number,
): string => {
  return (parseInt(String(i)) + count - 1)
    .toString()
    .padStart(i.toString().length, "0");
};

export const dmsToDD = (coord: string): number => {
  const parts = coord.split(":");
  if (parts[0].indexOf("-") !== -1) {
    return (
      parseFloat(parts[0]) -
      parseFloat(parts[1]) / 60 -
      parseFloat(parts[2]) / 3600
    );
  } else {
    return (
      parseFloat(parts[0]) +
      parseFloat(parts[1]) / 60 +
      parseFloat(parts[2]) / 3600
    );
  }
};

export const dataURLtoFile = (dataUrl: string, filename: string): File => {
  let arr = dataUrl.split(",");
  let mime = arr[0].match(/:(.*?);/)![1];
  let bstr = atob(arr[1]);
  let n = bstr.length;
  let u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new File([u8arr], filename, { type: mime });
};

export const resolveObjectProperty = (
  path: string | string[],
  obj: any,
  separator: string = ".",
): any => {
  const properties = Array.isArray(path) ? path : path.split(separator);
  return properties.reduce(
    (prev: any, curr: string) => prev && prev[curr],
    obj,
  );
};

export const stringToColor = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  let colour = "#";
  for (let i = 0; i < 3; i++) {
    let value = (hash >> (i * 8)) & 0xff;
    colour += ("00" + value.toString(16)).substr(-2);
  }
  return colour;
};

export const parseJWT = (token: string): any => {
  const base64Payload = token.split(".")[1];
  const payload = Buffer.from(base64Payload, "base64");
  return JSON.parse(payload.toString());
};

export function bytesToGB(bytes: number): number {
  const GB = 1024 * 1024 * 1024; // 1 GB in bytes
  return bytes / GB;
}
